import { List as ImmutableList, type Map as ImmutableMapType } from 'immutable';
import { DateTime, DateTimeUnit, Interval } from 'luxon';

import { transactionsUtils } from 'companion-app-components/flux/transactions';
import type { CashFlowTransaction, SplitItemProps } from 'companion-app-components/flux/transactions/transactionsTypes';
import type { Category } from 'companion-app-components/flux/categories/categoriesTypes';
import type { Tag } from 'companion-app-components/flux/tags/tagsTypes';

import { getCurrencyForAccount, getCombinedCurrency } from 'components/QCurrency/utils';
import { isUnAcceptedTxnType, isC2RTransaction } from 'data/transactions/utils';
import { OTReturn, type OTReturnRecord, type FrequencyData, type FilterFns } from './types';

// ================================================================================================
// Txn Filters
// ================================================================================================

// Filter to determine whether a txn is a candidate for reportable income. To be a candidate, txn must fall within the
// date interval and either have a regular CATEGORY or be a split-transaction.
//

export const isPostedOnDateInterval = (txn: CashFlowTransaction, dateInterval: Interval) => {
  const postedOn = DateTime.fromISO(txn.postedOn);
  return !(!txn || !dateInterval.contains(postedOn));
};

function isTxnCandidateForReportableIncome(txn: CashFlowTransaction, dateInterval: Interval) {
  const postedOn = DateTime.fromISO(txn.postedOn);
  if (!dateInterval.contains(postedOn)) {
    return false;
  }
  return ((txn.coa && txn.coa.type === 'CATEGORY') || transactionsUtils.isSplitTxn(txn));
}

// Filter to determine whether a txn is a candidate for reportable expense. To be a candidate, txn must fall within the
// date interval and either have a regular CATEGORY, be UNCATEGORIZED or be a split-transaction.
function isTxnCandidateForReportableSpending(txn: CashFlowTransaction, dateInterval: Interval) {
  const postedOn = DateTime.fromISO(txn.postedOn);
  if (!dateInterval.contains(postedOn)) {
    return false;
  }
  return ((txn.coa && (txn.coa.type === 'CATEGORY' || txn.coa.type === 'UNCATEGORIZED')) || transactionsUtils.isSplitTxn(txn));
}

export const isTxnItemReportableSpending = (item: SplitItemProps, categoriesById: ImmutableMapType<string, Category>) => {
  if (!item.coa) {
    return false;
  }
  if (item.coa.type && item.coa.type === 'UNCATEGORIZED') {
    return true;
  }
  const category = categoriesById.get(item.coa && item.coa.id);
  return (category && category.type === 'EXPENSE') || false;
};

export const isTxnItemReportableIncome = (item: SplitItemProps, categoriesById: ImmutableMapType<string, Category>): boolean => {
  if (!item.coa) {
    return false;
  }
  const category = categoriesById.get(item.coa.id);
  return (category && category.type === 'INCOME') || false;
};

export const incomeFilters = {
  isTxnCandidateForReporting: isTxnCandidateForReportableIncome,
  isTxnItemReportable: isTxnItemReportableIncome,
};

function isTxnItemReportableWatchlist(item: SplitItemProps, categoriesById: ImmutableMapType<string, Category>) {
  if (!item.coa) {
    return false;
  }
  return isTxnItemReportableIncome(item, categoriesById) || isTxnItemReportableSpending(item, categoriesById) ||
    item?.coa?.type === 'BALANCE_ADJUSTMENT';
}

export const watchlistFilters = {
  isTxnCandidateForReporting: (txn: CashFlowTransaction, dateInterval: Interval) => isTxnCandidateForReportableIncome(txn, dateInterval) || isTxnCandidateForReportableSpending(txn, dateInterval),
  isTxnItemReportable: isTxnItemReportableWatchlist,
};

function payeesMatch(payee1?: string | null, payee2?: string | null) {
  if (payee1 && payee2) {
    const normalized1 = payee1?.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
    const normalized2 = payee2?.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');

    return normalized1 === normalized2;
  }
  return false;
}

function isTxnHasCategory(txn: CashFlowTransaction, category: string) {
  return txn.coa && txn.coa.id === category;
}

function isTxnHasTag(txn: CashFlowTransaction, tag: Tag) {
  if (txn.tags) {
    return !txn.tags.every((theTag) => theTag.id !== tag.id);
  }
  return false;
}

// parameter "txn" might not be a real txn, it might be a split item (which has no payee field), so caller has to pass in the txnPayee
function isTxnExcludedByWatchlistFilter(txn: CashFlowTransaction, filterCriteria: any, txnPayee: string | null = null) {
  let exclude = false;
  if (filterCriteria) {
    switch (filterCriteria.type) {
      case 'payees': exclude = filterCriteria.items.every((payee) => !payeesMatch(txn.payee || txnPayee, payee)); break;
      case 'categories': exclude = filterCriteria.items.every((category) => !isTxnHasCategory(txn, category)); break;
      case 'tags': exclude = filterCriteria.items.every((tag) => !isTxnHasTag(txn, tag)); break;
      default: break;
    }
  }
  return exclude;
}

function isTxnExcludedByFilterCriteriaTopLevelCategoryConstraint(txn, filterCriteria) {
  let exclude = false;
  if (filterCriteria && filterCriteria.type === 'categories' && filterCriteria.topLevelOnly) {
    exclude = !isTxnHasCategory(txn, filterCriteria.parentCoaNodeId);
  }
  return exclude;
}

function isTxnExcludedBySource(txn, filterBySource) {
  let exclude = false;
  if (filterBySource) {
    // console.log(`(AAA) ${txn.payee}: ${txn.source} === ${filterBySource} | ${txn.source === filterBySource}`);
    exclude = (txn.source === filterBySource);
  }
  return exclude;
}

export const spendingFilters = {
  isTxnCandidateForReporting: isTxnCandidateForReportableSpending,
  isTxnItemReportable: isTxnItemReportableSpending,
};

// ================================================================================================
// Frequency Data
// ================================================================================================

export const monthlyFrequencyData: FrequencyData = {
  frequency: 'month',
  duration: 'months',
  keyFromPeriod: (period) => period.toFormat('yyyy.MM'),
  shortLabelFromPeriod: (period) => period.toFormat('MMM'),
  labelFromPeriod: (period) => period.toFormat('MMMM'),
  yearFromPeriod: (period) => period.toFormat('yyyy'),
};

// ================================================================================================
// Calculators
// ================================================================================================
const isTxnReportable = (
  txn: CashFlowTransaction,
  filter: FilterFns,
  filterCriteria: any,
  filterBySource: string | null,
  categoriesById: ImmutableMapType<string, Category>,
  payee: string | null = null,
) => {
  const test1 = filter.isTxnItemReportable(txn, categoriesById);
  const test2 = !isTxnExcludedByWatchlistFilter(txn, filterCriteria, payee);
  const test3 = !isTxnExcludedByFilterCriteriaTopLevelCategoryConstraint(txn, filterCriteria);
  const test4 = !isTxnExcludedBySource(txn, filterBySource);
  return test1 && test2 && test3 && test4;
};

const isTxnNotExcluded = (txn: CashFlowTransaction) => !txn.isExcludedFromReports;

export const getReportableTxn = (
  txn: CashFlowTransaction,
  categoriesById: ImmutableMapType<string, Category>,
  filterCriteria: any,
  filter: FilterFns,
  filterBySource: string | null,
  isReportableTxn: typeof isTxnReportable = isTxnReportable,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isTxnNotExcludedFromReports = isTxnNotExcluded, // eslint-disable-line no-unused-vars
) => {

  let amount = 0;
  let isReportable = false;

  if (!transactionsUtils.isSplitTxn(txn)) {
    if (isReportableTxn(txn, filter, filterCriteria, filterBySource, categoriesById, txn.payee)) {
      // We don't want to add scheduled transaction pending to the totals
      const txnDate = DateTime.fromISO(txn.postedOn);
      const isUnAcceptedTxnTypeTxn = isUnAcceptedTxnType(txn);
      const txnDiff = txnDate?.diff(DateTime.local(), 'days').toObject().days;
      if (!isUnAcceptedTxnTypeTxn && !(txnDiff && txnDiff >= 0)) {
        amount = Number(txn.amount);
      }
      isReportable = Boolean(!isUnAcceptedTxnTypeTxn);
    }
  } else {
    const isUnAcceptedTxnTypeTxn = isUnAcceptedTxnType(txn);
    if (!isUnAcceptedTxnTypeTxn) {
      let splitHadReportable = false;

      const txnDate = DateTime.fromISO(txn.postedOn);
      txn.split?.items?.forEach((item) => {
        if (isReportableTxn(item, filter, filterCriteria, filterBySource, categoriesById, txn.payee)) {
          const splitDiff = txnDate?.diff(DateTime.local(), 'days').toObject().days;
          if (!(splitDiff && splitDiff >= 0)) {
            amount += Number(item.amount);
          }
          splitHadReportable = true;
        }
      });
      if (splitHadReportable) {
        isReportable = true;
      }
    }
  }

  return { amount, isReportable };
};

export const calculateValuesForPeriods = (
  accountIds: ImmutableList<any> | any,
  accountsByIds: ImmutableMapType<string, any>,
  categoriesById: ImmutableMapType<string, Category>,
  transactionsByAccountId: ImmutableMapType<string, any>,
  dateInterval: Interval,
  frequencyData: FrequencyData,
  filter: FilterFns,
  filterCriteria: any,
  filterBySource: string | null,
): OTReturnRecord => {

  const periods = new Map();
  accountIds.forEach((accountId) => {
    const account = accountsByIds.get(accountId);
    const currency = account ? getCurrencyForAccount(account) : '';

    if (!['BANK', 'CREDIT', 'CASH'].includes(account.type)) return;  // loan and asset account txns are to be filtered out

    const txns = transactionsByAccountId.get(accountId);
    (txns || []).forEach((txn) => {
      const period = DateTime.fromISO(txn.postedOn).startOf(frequencyData.frequency as DateTimeUnit);
      const key = frequencyData.keyFromPeriod(period);
      const isC2RTxn = isC2RTransaction(txn);

      let data = periods.get(key);
      if (!data) {
        periods.set(key, data = {
          period,
          amount: 0,
          txns: [],
          currency,
        });
      }

      const { amount, isReportable } = getReportableTxn(
        txn,
        categoriesById,
        filterCriteria,
        filter,
        filterBySource,
      );
      
      if (!isC2RTxn) {
        data.amount += amount;
      }

      if (isReportable && !isC2RTxn) {
        data.txns.push(txn);
      }
    });
  });

  const byPeriods: Record<string, any>[] = [];
  let txns: CashFlowTransaction[] = [];
  let currency = '';

  // Create byPeriods array in date-order (start->end) and all txns array. Will add values for periods with no income,
  // to insure we have keys for every period in date interval.
  //
  for (
    let period = dateInterval.start?.startOf(frequencyData.frequency as DateTimeUnit) as DateTime;
    dateInterval.contains(period);
    period = period.plus({ [frequencyData.duration]: 1 })
  ) {
    const key = frequencyData.keyFromPeriod(period);
    const dataForPeriod = periods.get(key);

    const byPeriod = {
      period,

      label: frequencyData.labelFromPeriod(period),
      shortLabel: frequencyData.shortLabelFromPeriod(period),
      year: frequencyData.yearFromPeriod(period),
      amount: dataForPeriod ? dataForPeriod.amount : 0,
      currency: dataForPeriod ? dataForPeriod.currency : '',
      txns: ImmutableList(dataForPeriod ? dataForPeriod.txns : []),
    };
    byPeriods.push(byPeriod);

    txns = txns.concat(dataForPeriod ? dataForPeriod.txns : []);
    currency = getCombinedCurrency(currency, dataForPeriod ? dataForPeriod.currency : '');
  }

  return new OTReturn({
    periods: ImmutableList(byPeriods),
    txns: ImmutableList(txns),
    currency,
  });
};

// NOTE: if an injectedKey is an object each period will have the same object in it
export function createPastMonthPeriods(injectedKeys: Record<string, any>, numMonths: number) {
  let periods = ImmutableList<Record<string, any>>();
  for (let i = 0; i < numMonths; i++) {
    const month = DateTime.local().endOf('month').minus({ months: i });
    periods = periods.set(i, {
      monthLong: month.monthLong,
      monthShort: month.monthShort,
      year: month.year,
      date: month.toISO(),
      txns: ImmutableList(),
      mutableTxns: [],
      ...injectedKeys,
    });
  }
  return periods.reverse();
}
